import { useEffect, useMemo, useState } from 'react';
import { GridInitialState } from '@mui/x-data-grid-pro';
import {
  fetchUnmappedCustomers,
  getUnmappedCustomersSubject,
  UnmappedCustomer
} from '../../../../../store/CustomerMap';
import { DefaultUnmappedCustomerGridColumnDefs } from '../../ColumnDefs/UnmappedCustomerGridColumnDefs';
import { UpstackDataGrid } from '../../../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { getInitialState } from '../../../../Compounds/UpstackDataGrid/helpers';
import { PageCard } from '../../../../Compounds/CardWithTitle';
import {
  getCustomizationSubject,
  getDefaultPreferences,
  GridPreferences
} from '../../../../../store/UserCustomizations';
import { ComponentSpinner } from '../../../../Compounds/Loading/ComponentSpinner';
import { NewMapDialog } from './NewMapDialog';
// import { EntityType } from '../../../../../store/Search';
import { ApiAdvancedFilter, PaginatedResponse } from '../../../../../store/GeneralStore';

export const UnmappedCustomerTab = () => {
  const [unmappedCustomersLoading, setUnmappedCustomersLoading] = useState<boolean>(false);
  const [unmappedCustomers, setUnmappedCustomers] = useState<PaginatedResponse<UnmappedCustomer>>();
  const [gridSettings, setGridSettings] = useState<GridPreferences>(
    getDefaultPreferences()?.content['unmappedCustomerGrid'] as GridPreferences
  );
  const [initialState, setInitialState] = useState<GridInitialState>();
  const [activeCustomer, setActiveCustomer] = useState<UnmappedCustomer | null>(null);

  const openModal = (customer: UnmappedCustomer) => {
    setActiveCustomer(customer);
  };

  const columns = DefaultUnmappedCustomerGridColumnDefs({ openModal });

  const filterKeys = useMemo(
    () => columns.filter((column) => column.filterable).map((column) => column.field),
    [columns]
  );

  const handleFetchCustomerMaps = async (filters?: ApiAdvancedFilter, localLoading?: boolean, forceRefresh = false) => {
    if (localLoading) setUnmappedCustomersLoading(true);
    await fetchUnmappedCustomers(filters, forceRefresh);
    if (localLoading) setUnmappedCustomersLoading(false);
  };

  useEffect(() => {
    const unmappedCustomersSub = getUnmappedCustomersSubject().subscribe((unmappedCustomers) =>
      setUnmappedCustomers(unmappedCustomers)
    );
    const userCustSub = getCustomizationSubject().subscribe((userCust) => {
      if (userCust) {
        setGridSettings(userCust.content.unmappedCustomerGrid);

        const state = getInitialState(userCust.content.unmappedCustomerGrid, columns, 'unmappedCustomerGrid');
        setInitialState(state);

        const savedSort = state.sorting?.sortModel;
        const initialSortModel = savedSort?.length ? savedSort : undefined;
        handleFetchCustomerMaps(
          {
            orders: initialSortModel
              ? initialSortModel.map((sort) => ({ sort: sort.field, direction: sort.sort }))
              : undefined
          },
          true
        );
      }
    });

    return () => {
      if (userCustSub) userCustSub.unsubscribe();
      if (unmappedCustomersSub) unmappedCustomersSub.unsubscribe();
    };
  }, []);

  return (
    <PageCard
      dataTestId="unmapped-customer-management"
      dataCy="unmapped-customer-management"
      childrenWrapperClass="min-h-80 max-w-[60vw] min-w-full"
      title="Unmapped RPM Customers (with items in latest run)">
      <>
        {!initialState ||
          (unmappedCustomersLoading && (
            <div className="flex h-[45rem] m-auto">
              <ComponentSpinner label="Loading data..." />
            </div>
          ))}
        {initialState && !unmappedCustomersLoading && (
          <UpstackDataGrid
            title="Customer Map"
            rows={unmappedCustomers?.data || []}
            rowCount={unmappedCustomers?.totalCount}
            columns={columns}
            advancedFiltering={true}
            apiFilter={true}
            fetchFunction={handleFetchCustomerMaps}
            filterKeys={filterKeys}
            page="unmappedCustomerGrid"
            gridSettings={gridSettings}
            showSearch={false}
            initialState={initialState}
            sxStyles={{ borderWidth: '0', borderBottomLeftRadius: '.75rem', borderBottomRightRadius: '.75rem' }}
            triggerSave={false}
            rowPrimaryKey="customerId"
          />
        )}
        <NewMapDialog
          open={!!activeCustomer}
          onClose={() => setActiveCustomer(null)}
          rpmCustomerPrefill={
            activeCustomer ? { id: String(activeCustomer.customerId), name: activeCustomer.customerName } : undefined
          }
        />
      </>
    </PageCard>
  );
};
